import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import GlAuditLink from '../components/GlAuditLink';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Trash2, Save, Users, Clock, FileText, Wallet, Loader2, Download, FileDown,
  CheckCircle2, XCircle, Edit, Banknote, ArrowRight, Mail, Printer,
} from 'lucide-react';
import { keyed, stripUids, withUids } from '../lib/listKeys';

const CONTRACT_TYPES = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'locum', label: 'Locum / per-hour' },
];
const PAY_BASES = [
  { value: 'monthly_salary', label: 'Monthly salary' },
  { value: 'hourly_rate', label: 'Hourly rate' },
];
const CURRENCIES = ['GHS', 'USD', 'EUR'];

const fmtMoney = (v, cur = 'GHS') => `${cur} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ─────────────────────────────────────────────────────────────────────
// Wrapper page — picks the active tab from the URL
// ─────────────────────────────────────────────────────────────────────
export default function HrPage() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = tab || 'employees';

  return (
    <div className="space-y-6" data-testid="hr-page">
      <div>
        <h1 className="text-2xl font-bold">HR &amp; Payroll</h1>
        <p className="text-sm text-muted-foreground">Employees, attendance, leave and Ghana-statutory payroll</p>
      </div>

      <Tabs value={active} onValueChange={(v) => navigate(`/hr/${v}`)} className="space-y-4">
        <TabsList className="grid grid-cols-4 max-w-2xl">
          <TabsTrigger value="employees" data-testid="tab-employees"><Users className="h-3.5 w-3.5 mr-1.5" />Employees</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance"><Clock className="h-3.5 w-3.5 mr-1.5" />Attendance</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave"><FileText className="h-3.5 w-3.5 mr-1.5" />Leave</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll"><Wallet className="h-3.5 w-3.5 mr-1.5" />Payroll</TabsTrigger>
        </TabsList>
        <TabsContent value="employees"><EmployeesTab /></TabsContent>
        <TabsContent value="attendance"><AttendanceTab /></TabsContent>
        <TabsContent value="leave"><LeaveTab /></TabsContent>
        <TabsContent value="payroll"><PayrollTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab 1 — Employees
// ─────────────────────────────────────────────────────────────────────
function EmployeesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {} | employee

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/hr/employees', { params: { active_only: false } });
      setList(data.employees || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setEditor(null); load(); };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Employees</CardTitle>
          <CardDescription>Full-time, part-time and locum staff</CardDescription>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setEditor({})} data-testid="add-employee-btn">
          <Plus className="h-4 w-4" /> Add Employee
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : list.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No employees yet. Click "Add Employee" to onboard the first one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">Contract</th>
                  <th className="text-left py-2 px-2">Pay basis</th>
                  <th className="text-right py-2 px-2">Rate</th>
                  <th className="text-left py-2 px-2">Department</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((e) => (
                  <tr key={e.employee_id} className="border-b hover:bg-muted/30" data-testid={`emp-row-${e.employee_id}`}>
                    <td className="py-2 px-2 font-medium">{e.name}</td>
                    <td className="py-2 px-2"><Badge variant="outline" className="text-xs">{(e.contract_type || '').replace('_', ' ')}</Badge></td>
                    <td className="py-2 px-2 text-xs">{e.pay_basis === 'hourly_rate' ? 'Hourly' : 'Monthly'}</td>
                    <td className="py-2 px-2 text-right tabular-nums">
                      {e.pay_basis === 'hourly_rate'
                        ? `${fmtMoney(e.hourly_rate, e.currency)}/hr`
                        : fmtMoney(e.basic_salary, e.currency)}
                    </td>
                    <td className="py-2 px-2 text-xs text-muted-foreground">{e.department || '—'}</td>
                    <td className="py-2 px-2"><Badge variant={e.active ? 'default' : 'secondary'} className="text-xs">{e.active ? 'Active' : 'Inactive'}</Badge></td>
                    <td className="py-2 px-2 text-right">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditor(e)} data-testid={`edit-emp-${e.employee_id}`}><Edit className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <EmployeeEditor open={editor !== null} initial={editor} onClose={() => setEditor(null)} onSaved={onSaved} />
    </Card>
  );
}

function EmployeeEditor({ open, initial, onClose, onSaved }) {
  const isEdit = initial && initial.employee_id;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(() => {
      const next = {
        name: '', email: '', phone: '',
        contract_type: 'full_time', pay_basis: 'monthly_salary',
        basic_salary: 0, hourly_rate: 0, currency: 'GHS',
        department: '', job_title: '', hire_date: '', ssnit_number: '', tin: '',
        ssnit_enrolled: null,
        bank: { bank_name: '', account_number: '', branch: '' },
        allowances: [], default_deductions: [],
        annual_leave_days: 15, active: true, notes: '',
        ...(initial || {}),
      };
      return { ...next, allowances: withUids(next.allowances), default_deductions: withUids(next.default_deductions) };
    });
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setBank = (k, v) => setForm((f) => ({ ...f, bank: { ...(f.bank || {}), [k]: v } }));
  const addAllowance = () => set('allowances', [...(form.allowances || []), { uid: crypto.randomUUID(), name: '', amount: 0, taxable: true }]);
  const setAllowance = (idx, k, v) => set('allowances', form.allowances.map((a, i) => i === idx ? { ...a, [k]: v } : a));
  const removeAllowance = (idx) => set('allowances', form.allowances.filter((_, i) => i !== idx));
  const addDeduction = () => set('default_deductions', [...(form.default_deductions || []), { uid: crypto.randomUUID(), name: '', amount: 0 }]);
  const setDeduction = (idx, k, v) => set('default_deductions', form.default_deductions.map((d, i) => i === idx ? { ...d, [k]: v } : d));
  const removeDeduction = (idx) => set('default_deductions', form.default_deductions.filter((_, i) => i !== idx));

  // Auto-flip pay_basis when contract = locum
  useEffect(() => {
    if (form.contract_type === 'locum' && form.pay_basis !== 'hourly_rate') {
      setForm((f) => ({ ...f, pay_basis: 'hourly_rate' }));
    }
  }, [form.contract_type, form.pay_basis]);

  const submit = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ['basic_salary', 'hourly_rate', 'annual_leave_days'].forEach((k) => { payload[k] = Number(payload[k] || 0); });
      payload.allowances = stripUids(payload.allowances).map((a) => ({ ...a, amount: Number(a.amount || 0) }));
      payload.default_deductions = stripUids(payload.default_deductions).map((d) => ({ ...d, amount: Number(d.amount || 0) }));
      if (isEdit) {
        await api.put(`/hr/employees/${form.employee_id}`, payload);
        toast.success('Employee updated');
      } else {
        await api.post('/hr/employees', payload);
        toast.success('Employee added');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="emp-editor">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit employee' : 'Add employee'}</DialogTitle>
          <DialogDescription>Profile, contract, salary and statutory IDs</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Identity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Full name *"><Input value={form.name || ''} onChange={(e) => set('name', e.target.value)} data-testid="emp-name-input" /></Field>
            <Field label="Job title"><Input value={form.job_title || ''} onChange={(e) => set('job_title', e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} /></Field>
            <Field label="Department"><Input value={form.department || ''} onChange={(e) => set('department', e.target.value)} /></Field>
            <Field label="Hire date"><Input type="date" value={form.hire_date || ''} onChange={(e) => set('hire_date', e.target.value)} /></Field>
          </div>

          {/* Contract */}
          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm font-semibold">Contract & Pay</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Contract type">
                <Select value={form.contract_type} onValueChange={(v) => set('contract_type', v)}>
                  <SelectTrigger data-testid="emp-contract-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{CONTRACT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Pay basis">
                <Select value={form.pay_basis} onValueChange={(v) => set('pay_basis', v)} disabled={form.contract_type === 'locum'}>
                  <SelectTrigger data-testid="emp-pay-basis"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_BASES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => set('currency', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {form.pay_basis === 'monthly_salary' ? (
                <Field label="Basic salary (monthly)">
                  <Input type="number" min="0" step="0.01" value={form.basic_salary} onChange={(e) => set('basic_salary', e.target.value)} data-testid="emp-basic-salary" />
                </Field>
              ) : (
                <Field label="Hourly rate">
                  <Input type="number" min="0" step="0.01" value={form.hourly_rate} onChange={(e) => set('hourly_rate', e.target.value)} data-testid="emp-hourly-rate" />
                </Field>
              )}
              <Field label="SSNIT #"><Input value={form.ssnit_number || ''} onChange={(e) => set('ssnit_number', e.target.value)} /></Field>
              <Field label="TIN"><Input value={form.tin || ''} onChange={(e) => set('tin', e.target.value)} /></Field>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="ssnit-toggle"
                checked={!!form.ssnit_enrolled}
                onCheckedChange={(v) => set('ssnit_enrolled', !!v)}
                data-testid="emp-ssnit-enrolled"
              />
              <Label htmlFor="ssnit-toggle" className="text-xs cursor-pointer">
                Apply SSNIT contributions {form.contract_type === 'locum' ? '(default OFF for locums)' : '(default ON)'}
              </Label>
            </div>
          </div>

          {/* Bank */}
          <div className="rounded-lg border p-3 space-y-3">
            <p className="text-sm font-semibold">Bank — for payroll bank file</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Bank name"><Input value={form.bank?.bank_name || ''} onChange={(e) => setBank('bank_name', e.target.value)} /></Field>
              <Field label="Account number"><Input value={form.bank?.account_number || ''} onChange={(e) => setBank('account_number', e.target.value)} /></Field>
              <Field label="Branch"><Input value={form.bank?.branch || ''} onChange={(e) => setBank('branch', e.target.value)} /></Field>
            </div>
          </div>

          {/* Allowances */}
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Allowances (recurring)</p>
              <Button size="sm" variant="outline" onClick={addAllowance} data-testid="add-allowance-btn"><Plus className="h-3 w-3 mr-1" />Add</Button>
            </div>
            {(form.allowances || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">None — only basic salary will apply.</p>
            ) : (form.allowances || []).map((a, i) => (
              <div key={a.uid} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-5" placeholder="Name (e.g. Transport)" value={a.name} onChange={(e) => setAllowance(i, 'name', e.target.value)} />
                <Input className="col-span-3" type="number" placeholder="Amount" value={a.amount} onChange={(e) => setAllowance(i, 'amount', e.target.value)} />
                <div className="col-span-3 flex items-center gap-2">
                  <Switch checked={!!a.taxable} onCheckedChange={(v) => setAllowance(i, 'taxable', v)} />
                  <span className="text-xs">{a.taxable ? 'Taxable' : 'Non-tax'}</span>
                </div>
                <Button variant="ghost" size="sm" className="col-span-1 h-8" onClick={() => removeAllowance(i)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
              </div>
            ))}
          </div>

          {/* Default deductions */}
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">Recurring deductions (loans, advances)</p>
              <Button size="sm" variant="outline" onClick={addDeduction}><Plus className="h-3 w-3 mr-1" />Add</Button>
            </div>
            {(form.default_deductions || []).length === 0 ? (
              <p className="text-xs text-muted-foreground">None.</p>
            ) : (form.default_deductions || []).map((d, i) => (
              <div key={d.uid} className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-7" placeholder="Name (e.g. Loan repayment)" value={d.name} onChange={(e) => setDeduction(i, 'name', e.target.value)} />
                <Input className="col-span-4" type="number" placeholder="Amount" value={d.amount} onChange={(e) => setDeduction(i, 'amount', e.target.value)} />
                <Button variant="ghost" size="sm" className="col-span-1 h-8" onClick={() => removeDeduction(i)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Switch id="emp-active" checked={!!form.active} onCheckedChange={(v) => set('active', v)} />
            <Label htmlFor="emp-active" className="text-xs cursor-pointer">Employee is currently active</Label>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="flex-1 gap-1.5" data-testid="emp-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? 'Save changes' : 'Add employee'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab 2 — Attendance (per period entry grid)
// ─────────────────────────────────────────────────────────────────────
function AttendanceTab() {
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState({}); // employee_id -> attendance row
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, attRes] = await Promise.all([
        api.get('/hr/employees', { params: { active_only: true } }),
        api.get('/hr/attendance', { params: { period } }),
      ]);
      setEmployees(empRes.data.employees || []);
      const map = {};
      (attRes.data.attendance || []).forEach((a) => { map[a.employee_id] = a; });
      setRows(map);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const setCell = (eid, field, value) => {
    setRows((prev) => ({ ...prev, [eid]: { ...(prev[eid] || {}), [field]: value } }));
  };

  const save = async (eid) => {
    const r = rows[eid] || {};
    try {
      await api.post('/hr/attendance', {
        employee_id: eid,
        period,
        days_worked: r.days_worked === '' ? null : (r.days_worked != null ? Number(r.days_worked) : null),
        hours_worked: r.hours_worked === '' ? null : (r.hours_worked != null ? Number(r.hours_worked) : null),
        overtime_hours: r.overtime_hours ? Number(r.overtime_hours) : null,
        overtime_rate: r.overtime_rate ? Number(r.overtime_rate) : null,
        notes: r.notes || '',
      });
      toast.success('Saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-end justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Attendance</CardTitle>
          <CardDescription>Pick a period (YYYY-MM) and record days or hours worked per employee</CardDescription>
        </div>
        <div className="flex items-end gap-2">
          <Field label="Period">
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-[160px]" data-testid="att-period-input" />
          </Field>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : employees.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Add employees first.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b">
                <tr>
                  <th className="text-left py-2 px-2">Employee</th>
                  <th className="text-left py-2 px-2">Pay basis</th>
                  <th className="text-right py-2 px-2 w-24">Days</th>
                  <th className="text-right py-2 px-2 w-24">Hours</th>
                  <th className="text-right py-2 px-2 w-24">OT hrs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {employees.map((e) => {
                  const r = rows[e.employee_id] || {};
                  const isHourly = e.pay_basis === 'hourly_rate';
                  return (
                    <tr key={e.employee_id} className="border-b" data-testid={`att-row-${e.employee_id}`}>
                      <td className="py-2 px-2"><span className="font-medium">{e.name}</span> <Badge variant="outline" className="ml-1 text-[10px]">{e.contract_type}</Badge></td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">{isHourly ? 'Hourly' : 'Monthly'}</td>
                      <td className="py-2 px-2"><Input type="number" min="0" max="31" className="h-8 text-right" value={r.days_worked ?? ''} onChange={(ev) => setCell(e.employee_id, 'days_worked', ev.target.value)} disabled={isHourly} placeholder={isHourly ? '—' : '30'} /></td>
                      <td className="py-2 px-2"><Input type="number" min="0" className="h-8 text-right" value={r.hours_worked ?? ''} onChange={(ev) => setCell(e.employee_id, 'hours_worked', ev.target.value)} disabled={!isHourly} placeholder={!isHourly ? '—' : '0'} data-testid={`att-hours-${e.employee_id}`} /></td>
                      <td className="py-2 px-2"><Input type="number" min="0" className="h-8 text-right" value={r.overtime_hours ?? ''} onChange={(ev) => setCell(e.employee_id, 'overtime_hours', ev.target.value)} placeholder="0" /></td>
                      <td className="py-2 px-2 text-right"><Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => save(e.employee_id)} data-testid={`att-save-${e.employee_id}`}><Save className="h-3.5 w-3.5" />Save</Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab 3 — Leave Requests
// ─────────────────────────────────────────────────────────────────────
function LeaveTab() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [creating, setCreating] = useState(null); // null or new request form
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lvRes, empRes] = await Promise.all([
        api.get('/hr/leave'),
        api.get('/hr/employees', { params: { active_only: true } }),
      ]);
      setList(lvRes.data.leave_requests || []);
      setEmployees(empRes.data.employees || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (lv, status) => {
    try {
      await api.put(`/hr/leave/${lv.leave_id}/decision`, { status });
      toast.success(`Leave ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Decision failed');
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Leave Requests</CardTitle>
          <CardDescription>Submit, approve and track time-off</CardDescription>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setCreating({ leave_type: 'annual' })} data-testid="add-leave-btn">
          <Plus className="h-4 w-4" /> New Request
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No leave requests yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b">
              <tr><th className="text-left py-2 px-2">Employee</th><th>Type</th><th>From</th><th>To</th><th>Days</th><th>Status</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {list.map((lv) => {
                const emp = employees.find((e) => e.employee_id === lv.employee_id);
                return (
                  <tr key={lv.leave_id} className="border-b" data-testid={`lv-row-${lv.leave_id}`}>
                    <td className="py-2 px-2 font-medium">{emp?.name || lv.employee_id}</td>
                    <td className="text-xs">{lv.leave_type}</td>
                    <td className="text-xs">{lv.start_date}</td>
                    <td className="text-xs">{lv.end_date}</td>
                    <td className="text-xs">{lv.days}</td>
                    <td><Badge variant={lv.status === 'approved' ? 'default' : lv.status === 'rejected' ? 'destructive' : 'secondary'} className="text-[10px]">{lv.status}</Badge></td>
                    <td className="text-right">
                      {lv.status === 'pending' && (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => decide(lv, 'approved')} data-testid={`lv-approve-${lv.leave_id}`}><CheckCircle2 className="h-3.5 w-3.5 text-green-600" />Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 gap-1" onClick={() => decide(lv, 'rejected')} data-testid={`lv-reject-${lv.leave_id}`}><XCircle className="h-3.5 w-3.5 text-red-600" />Reject</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
      <NewLeaveDialog open={creating !== null} initial={creating} employees={employees} onClose={() => setCreating(null)} onSaved={() => { setCreating(null); load(); }} />
    </Card>
  );
}

function NewLeaveDialog({ open, initial, employees, onClose, onSaved }) {
  const [form, setForm] = useState({ leave_type: 'annual' });
  const [saving, setSaving] = useState(false);
  const employeesRef = useRef(employees);
  employeesRef.current = employees;
  const initialRef = useRef(initial);
  initialRef.current = initial;

  useEffect(() => {
    if (open) setForm({ leave_type: 'annual', start_date: '', end_date: '', employee_id: employeesRef.current[0]?.employee_id, reason: '', ...(initialRef.current || {}) });
  }, [open]);

  const submit = async () => {
    if (!form.employee_id || !form.start_date || !form.end_date) { toast.error('Pick employee + start + end'); return; }
    setSaving(true);
    try {
      await api.post('/hr/leave', form);
      toast.success('Leave request submitted');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New leave request</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Employee">
            <Select value={form.employee_id || ''} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Pick employee" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Leave type">
            <Select value={form.leave_type} onValueChange={(v) => setForm((f) => ({ ...f, leave_type: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {['annual', 'sick', 'maternity', 'unpaid', 'other'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><Input type="date" value={form.start_date || ''} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} /></Field>
            <Field label="To"><Input type="date" value={form.end_date || ''} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} /></Field>
          </div>
          <Field label="Reason"><Textarea rows={2} value={form.reason || ''} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} /></Field>
        </div>
        <DialogFooter className="flex-row gap-2">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="flex-1">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tab 4 — Payroll Runs
// ─────────────────────────────────────────────────────────────────────
function PayrollTab() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/hr/payroll/runs');
      setRuns(data.runs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startRun = async () => {
    setCreating(true);
    try {
      const { data } = await api.post('/hr/payroll/runs', { period });
      toast.success(`Drafted ${data.headcount} payslips for ${period}`);
      setActive(data.run_id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-end justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Run Payroll</CardTitle>
            <CardDescription>Pulls active employees + recorded attendance for the chosen period and computes SSNIT + PAYE</CardDescription>
          </div>
          <div className="flex items-end gap-2">
            <Field label="Period"><Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="w-[160px]" data-testid="payroll-period-input" /></Field>
            <Button onClick={startRun} disabled={creating} className="gap-1.5" data-testid="run-payroll-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
              Compute draft
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Past runs</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : runs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No runs yet — pick a period above and click "Compute draft".</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b">
                <tr><th className="text-left py-2 px-2">Period</th><th>Status</th><th>Headcount</th><th>Net (per currency)</th><th></th></tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.run_id} className="border-b hover:bg-muted/30" data-testid={`run-row-${r.run_id}`}>
                    <td className="py-2 px-2 font-medium">{r.period}</td>
                    <td><Badge variant={r.status === 'paid' ? 'default' : r.status === 'finalized' ? 'secondary' : 'outline'} className="text-[10px]">{r.status}</Badge></td>
                    <td>{r.headcount}</td>
                    <td className="text-xs">{Object.entries(r.currency_breakdown || {}).map(([cur, b]) => <div key={cur}>{fmtMoney(b.net_pay, cur)}</div>)}</td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" className="h-7 gap-1.5" onClick={() => setActive(r.run_id)} data-testid={`view-run-${r.run_id}`}><ArrowRight className="h-3.5 w-3.5" />Open</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {active && <PayrollRunDetail runId={active} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function PayrollRunDetail({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hr/payroll/runs/${runId}`);
      setRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load run');
    }
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const finalize = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/payroll/runs/${runId}/finalize`, { confirm: true });
      toast.success('Payroll finalized');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalize failed');
    } finally { setBusy(false); }
  };
  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/hr/payroll/runs/${runId}/mark-paid`);
      toast.success('Marked as paid');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark paid');
    } finally { setBusy(false); }
  };
  // Show payslip data as HTML in a modal — no iframe, no blob URL, just JSX.
  // The PDF is still generated server-side and available via Download/Email/Print.
  const [pdfPreview, setPdfPreview] = useState(null); // { slip, employee }
  const [downloading, setDownloading] = useState(false);
  // Bulk email selection state
  const [selectedSlipIds, setSelectedSlipIds] = useState(new Set());
  const [bulkEmailDialog, setBulkEmailDialog] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkNote, setBulkNote] = useState('');
  const [skipMissingEmail, setSkipMissingEmail] = useState(true);

  const fetchAsBlob = async (path, mime) => {
    const res = await api.get(path, { responseType: 'blob' });
    return new Blob([res.data], { type: mime });
  };
  const saveBlobAsFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      window.URL.revokeObjectURL(url);
    }, 200);
  };

  const downloadBank = async () => {
    setDownloading(true);
    try {
      const blob = await fetchAsBlob(`/hr/payroll/runs/${runId}/export`, 'text/csv');
      saveBlobAsFile(blob, `payroll_${run?.period}_export.csv`);
      toast.success('Bank file downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed — check that the run is finalized');
    } finally {
      setDownloading(false);
    }
  };

  const openPayslip = async (slip) => {
    // Pull employee details so the HTML preview can show bank/SSNIT/TIN etc.
    let employee = {};
    try {
      const { data } = await api.get(`/hr/employees/${slip.employee_id}`);
      employee = data || {};
    } catch { /* fall back to whatever's on the slip */ }
    setPdfPreview({ slip, employee });
  };
  const closePreview = () => setPdfPreview(null);

  // ── Bulk email ──
  const allSlipIds = (run?.payslips || []).map((s) => s.payslip_id);
  const isAllSelected = allSlipIds.length > 0 && allSlipIds.every((id) => selectedSlipIds.has(id));
  const isSomeSelected = selectedSlipIds.size > 0 && !isAllSelected;

  const toggleAll = () => {
    if (isAllSelected) {
      setSelectedSlipIds(new Set());
    } else {
      setSelectedSlipIds(new Set(allSlipIds));
    }
  };
  const toggleOne = (id) => {
    setSelectedSlipIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const sendBulk = async () => {
    setBulkSending(true);
    setBulkResult(null);
    try {
      const payload = {
        payslip_ids: selectedSlipIds.size > 0 ? Array.from(selectedSlipIds) : undefined,
        body: bulkNote.trim() || undefined,
        skip_missing_email: skipMissingEmail,
      };
      const { data } = await api.post(`/hr/payroll/runs/${runId}/email-batch`, payload);
      setBulkResult(data);
      const t = data.totals || {};
      if ((t.sent || 0) > 0) {
        toast.success(`Sent ${t.sent} payslip${t.sent !== 1 ? 's' : ''}`, {
          description: `${t.failed || 0} failed · ${t.skipped || 0} skipped (no email on file)`,
        });
      } else {
        toast.warning('No payslips were sent', {
          description: `${t.failed || 0} failed · ${t.skipped || 0} skipped`,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk email failed');
    } finally {
      setBulkSending(false);
    }
  };

  return (
    <>
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="run-detail-dialog">
        <DialogHeader>
          <DialogTitle>Payroll · {run?.period}</DialogTitle>
          <DialogDescription>
            {run?.headcount} employees • status{' '}
            <span className="inline-block px-1.5 py-0.5 rounded border text-[10px] font-medium align-middle">{run?.status}</span>
          </DialogDescription>
          {run?.run_id && run?.status === 'paid' && (
            <div className="pt-1">
              <GlAuditLink source="payroll" sourceRef={run.run_id} />
            </div>
          )}
        </DialogHeader>

        {!run ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-12" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Gross" value={fmtMoney(run.totals?.gross_pay)} />
              <Stat label="SSNIT (employee)" value={fmtMoney(run.totals?.ssnit_employee)} />
              <Stat label="PAYE" value={fmtMoney(run.totals?.paye)} />
              <Stat label="Net pay" value={fmtMoney(run.totals?.net_pay)} highlight />
              <Stat label="Other deductions" value={fmtMoney(run.totals?.other_deductions)} />
              <Stat label="SSNIT (employer)" value={fmtMoney(run.totals?.ssnit_employer)} />
              <Stat label="Tier-2" value={fmtMoney(run.totals?.tier2_employer)} />
              <Stat label="Total employer cost" value={fmtMoney(run.totals?.employer_total)} />
            </div>

            <div className="overflow-x-auto mt-4">
              {/* Bulk action bar */}
              <div className="flex items-center justify-between gap-3 mb-2 px-1">
                <p className="text-xs text-muted-foreground">
                  {selectedSlipIds.size > 0
                    ? `${selectedSlipIds.size} selected of ${allSlipIds.length}`
                    : `${allSlipIds.length} payslip${allSlipIds.length !== 1 ? 's' : ''}`}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5"
                  onClick={() => setBulkEmailDialog(true)}
                  disabled={allSlipIds.length === 0}
                  data-testid="bulk-email-btn"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email {selectedSlipIds.size > 0 ? `${selectedSlipIds.size} selected` : 'all to employees'}
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-2 px-2 w-10">
                      <Checkbox
                        checked={isAllSelected ? true : (isSomeSelected ? 'indeterminate' : false)}
                        onCheckedChange={toggleAll}
                        data-testid="select-all-slips"
                        aria-label="Select all"
                      />
                    </th>
                    <th className="text-left py-2 px-2">Employee</th><th>Currency</th><th className="text-right">Gross</th><th className="text-right">SSNIT</th><th className="text-right">PAYE</th><th className="text-right">Net</th><th>Email</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {(run.payslips || []).map((s) => (
                    <tr key={s.payslip_id} className="border-b hover:bg-muted/20" data-testid={`slip-row-${s.payslip_id}`}>
                      <td className="py-2 px-2">
                        <Checkbox
                          checked={selectedSlipIds.has(s.payslip_id)}
                          onCheckedChange={() => toggleOne(s.payslip_id)}
                          data-testid={`select-slip-${s.payslip_id}`}
                        />
                      </td>
                      <td className="py-2 px-2 font-medium">{s.employee_name}</td>
                      <td className="text-xs">{s.currency}</td>
                      <td className="text-right tabular-nums">{fmtMoney(s.earnings?.gross_pay, s.currency)}</td>
                      <td className="text-right tabular-nums">{fmtMoney(s.deductions?.ssnit_employee, s.currency)}</td>
                      <td className="text-right tabular-nums">{fmtMoney(s.deductions?.paye, s.currency)}</td>
                      <td className="text-right tabular-nums font-semibold">{fmtMoney(s.net_pay, s.currency)}</td>
                      <td className="text-xs">
                        {s.email_status === 'sent' ? (
                          <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300">Sent</Badge>
                        ) : s.email_status === 'failed' ? (
                          <Badge variant="outline" className="text-[10px] text-red-600 border-red-300" title={s.email_error || ''}>Failed</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="text-right"><Button size="sm" variant="ghost" className="h-7 gap-1" onClick={() => openPayslip(s)} data-testid={`open-slip-${s.payslip_id}`}><FileDown className="h-3.5 w-3.5" />Open</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} className="mr-auto">Close</Button>
          {run?.status === 'draft' && (
            <Button onClick={finalize} disabled={busy} className="gap-1.5" data-testid="finalize-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Finalize
            </Button>
          )}
          {run?.status !== 'draft' && (
            <Button variant="outline" onClick={downloadBank} disabled={downloading} className="gap-1.5" data-testid="download-bank-csv-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Bank file (CSV)
            </Button>
          )}
          {run?.status === 'finalized' && (
            <Button onClick={markPaid} disabled={busy} className="gap-1.5" data-testid="mark-paid-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />} Mark paid
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <PayslipPreviewDialog
      open={!!pdfPreview}
      preview={pdfPreview}
      onClose={closePreview}
      runId={runId}
      runPeriod={run?.period}
    />
    {/* Bulk email dialog */}
    <Dialog open={bulkEmailDialog} onOpenChange={(o) => { if (!o) { setBulkEmailDialog(false); setBulkResult(null); } }}>
      <DialogContent className="max-w-lg" data-testid="bulk-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" /> Email payslips to employees
          </DialogTitle>
          <DialogDescription>
            {selectedSlipIds.size > 0
              ? `Send ${selectedSlipIds.size} selected payslip${selectedSlipIds.size !== 1 ? 's' : ''} via SMTP.`
              : `Send all ${allSlipIds.length} payslips in this run.`}
          </DialogDescription>
        </DialogHeader>

        {!bulkResult ? (
          <div className="space-y-3">
            <Field label="Optional message for employees">
              <Textarea
                rows={3}
                placeholder="e.g. Please review your payslip and reach out to HR with any questions."
                value={bulkNote}
                onChange={(e) => setBulkNote(e.target.value)}
                data-testid="bulk-email-note"
              />
            </Field>
            <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3">
              <Checkbox
                id="skip-missing"
                checked={skipMissingEmail}
                onCheckedChange={(v) => setSkipMissingEmail(!!v)}
                data-testid="skip-missing-email"
              />
              <Label htmlFor="skip-missing" className="text-xs cursor-pointer leading-relaxed">
                Skip employees without an email on file (recommended). Uncheck to count them as failures.
              </Label>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Each employee gets the branded payslip PDF as an attachment. Sending uses your configured SMTP and counts against your provider's send quota.
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Sent" value={String(bulkResult.totals?.sent ?? 0)} />
              <Stat label="Failed" value={String(bulkResult.totals?.failed ?? 0)} />
              <Stat label="Skipped" value={String(bulkResult.totals?.skipped ?? 0)} />
            </div>
            {(bulkResult.failed?.length || 0) > 0 && (
              <div>
                <p className="text-xs font-semibold text-red-600 mb-1">Failed</p>
                <ul className="text-xs space-y-1">
                  {keyed(bulkResult.failed, f => `${f.employee_name}-${f.error}`).map(({ item: f, key }) => (
                    <li key={key} className="rounded border border-red-200 bg-red-50 px-2 py-1">
                      <b>{f.employee_name}</b> — {f.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(bulkResult.skipped?.length || 0) > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1">Skipped</p>
                <ul className="text-xs space-y-1">
                  {keyed(bulkResult.skipped, s => `${s.employee_name}-${s.reason}`).map(({ item: s, key }) => (
                    <li key={key} className="rounded border bg-muted/30 px-2 py-1">
                      <b>{s.employee_name}</b> — {s.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(bulkResult.sent?.length || 0) > 0 && (
              <div>
                <p className="text-xs font-semibold text-emerald-600 mb-1">Sent successfully</p>
                <ul className="text-xs space-y-1">
                  {keyed(bulkResult.sent, s => `${s.employee_name}-${s.to_email}`).map(({ item: s, key }) => (
                    <li key={key} className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1">
                      <b>{s.employee_name}</b> → {s.to_email}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex-row gap-2">
          <Button variant="ghost" onClick={() => { setBulkEmailDialog(false); setBulkResult(null); }} className="flex-1">
            {bulkResult ? 'Close' : 'Cancel'}
          </Button>
          {!bulkResult && (
            <Button onClick={sendBulk} disabled={bulkSending} className="flex-1 gap-1.5" data-testid="bulk-send-btn">
              {bulkSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Send {selectedSlipIds.size > 0 ? `${selectedSlipIds.size}` : 'all'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

function PayslipPreviewDialog({ open, preview, onClose, runId, runPeriod }) {
  const { settings } = useSettings();
  const [emailing, setEmailing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [emailMode, setEmailMode] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');

  useEffect(() => {
    if (open) {
      setEmailMode(false);
      setEmailTo('');
      setEmailNote('');
    }
  }, [open]);

  const handlePrint = () => {
    if (!preview?.slip) return;
    const html = buildPayslipPrintDocument({
      slip: preview.slip,
      employee: preview.employee,
      period: runPeriod,
      settings,
    });
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) {
      toast.error('Allow popups for this site to print');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    // Wait for fonts/layout to settle, then print
    const triggerPrint = () => {
      try { w.print(); } catch { /* noop */ }
    };
    if (w.document.readyState === 'complete') {
      setTimeout(triggerPrint, 250);
    } else {
      w.addEventListener('load', () => setTimeout(triggerPrint, 200));
    }
  };

  const handleDownload = async () => {
    if (!preview?.slip) return;
    setDownloadingPdf(true);
    try {
      const res = await api.get(
        `/hr/payroll/runs/${runId}/slip/${preview.slip.payslip_id}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      const safe = (preview.slip.employee_name || 'employee').replace(/[^A-Za-z0-9_-]+/g, '_');
      a.download = `payslip_${runPeriod || ''}_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
        window.URL.revokeObjectURL(url);
      }, 200);
      toast.success('Payslip downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendEmail = async () => {
    setEmailing(true);
    try {
      const payload = {};
      if (emailTo.trim()) payload.to_email = emailTo.trim();
      if (emailNote.trim()) payload.body = emailNote.trim();
      const { data } = await api.post(
        `/hr/payroll/runs/${runId}/slip/${preview.slip.payslip_id}/email`,
        payload,
      );
      toast.success(`Payslip emailed to ${data.to_email}`);
      setEmailMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email failed');
    } finally {
      setEmailing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="payslip-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" /> Payslip · {preview?.slip?.employee_name}
          </DialogTitle>
          <DialogDescription>{runPeriod} · review, print, email or download</DialogDescription>
        </DialogHeader>

        {emailMode ? (
          <div className="flex-1 overflow-y-auto space-y-3 px-1">
            <Field label="Recipient email">
              <Input
                type="email"
                placeholder="Leave blank to send to employee's email on file"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                data-testid="payslip-email-to-input"
              />
            </Field>
            <Field label="Optional note">
              <Textarea
                rows={3}
                placeholder="Add a personal note (optional)"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
              />
            </Field>
            <p className="text-xs text-muted-foreground">A branded PDF copy of this payslip will be attached.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-1" data-testid="payslip-preview-content">
            <PayslipBody slip={preview?.slip} employee={preview?.employee} period={runPeriod} />
          </div>
        )}

        <DialogFooter className="flex-row flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} className="mr-auto">Close</Button>
          {emailMode ? (
            <>
              <Button variant="ghost" onClick={() => setEmailMode(false)}>Back</Button>
              <Button onClick={handleSendEmail} disabled={emailing} className="gap-1.5" data-testid="payslip-send-email-btn">
                {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send email
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handlePrint} className="gap-1.5" data-testid="payslip-print-btn">
                <Printer className="h-4 w-4" /> Print
              </Button>
              <Button variant="outline" onClick={() => setEmailMode(true)} className="gap-1.5" data-testid="payslip-email-btn">
                <Mail className="h-4 w-4" /> Email to employee
              </Button>
              <Button onClick={handleDownload} disabled={downloadingPdf} className="gap-1.5" data-testid="payslip-download-btn">
                {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                Download PDF
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayslipBody({ slip, employee, period }) {
  const { settings } = useSettings();
  if (!slip) return null;
  const cur = slip.currency || 'GHS';
  const earnings = slip.earnings || {};
  const deductions = slip.deductions || {};
  const employer = slip.employer_costs || {};
  const allowances = earnings.allowances || [];
  const otherItems = deductions.other_items || [];
  const bank = (employee?.bank) || (slip.bank) || {};
  const businessName = settings?.business_name || 'DutiLyne';
  const tagline = settings?.tagline || 'HR & Payroll';
  const logoUrl = settings?.logo_url || '';
  const primary = settings?.primary_color || '#0d9b8c';
  const accent = settings?.accent_color || '#e8590c';
  const address = settings?.business_address || '';
  const contactPhone = settings?.contact_phone || '';
  const contactEmail = settings?.contact_email || '';
  const issued = new Date().toLocaleDateString();

  return (
    <div className="bg-white text-slate-900 rounded-lg overflow-hidden border shadow-sm">
      {/* Hero header — branded */}
      <div
        className="px-6 py-5 flex items-start justify-between text-white"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -12)} 100%)` }}
      >
        <div className="flex items-start gap-3">
          {logoUrl ? (
            <img src={logoUrl} alt="logo" className="h-12 w-12 rounded bg-white/10 p-1 object-contain" />
          ) : (
            <div className="h-12 w-12 rounded bg-white/15 flex items-center justify-center text-lg font-bold">
              {businessName.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-lg font-bold leading-tight">{businessName}</div>
            <div className="text-xs opacity-80 mt-0.5">{tagline}</div>
            {address ? <div className="text-[10px] opacity-75 mt-1">{address}</div> : null}
            {(contactPhone || contactEmail) ? (
              <div className="text-[10px] opacity-75">
                {contactPhone}{contactPhone && contactEmail ? ' · ' : ''}{contactEmail}
              </div>
            ) : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold tracking-wide">PAYSLIP</div>
          <div className="text-xs opacity-90 mt-0.5">Period · {period}</div>
          <div className="text-[10px] opacity-80 mt-0.5">Issued · {issued}</div>
        </div>
      </div>

      {/* Identity card */}
      <div className="bg-slate-50 border-b px-6 py-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <Kv label="Employee" value={slip.employee_name || employee?.name} bold />
        <Kv label="Employee ID" value={slip.employee_id} />
        <Kv label="Department" value={employee?.department} />
        <Kv label="Contract" value={(slip.contract_type || 'full_time').replace('_', ' ')} cap />
        <Kv label="SSNIT #" value={employee?.ssnit_number} />
        <Kv label="TIN" value={employee?.tin} />
      </div>

      {/* Earnings + Deductions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-b">
        <div className="border-b md:border-b-0 md:border-r">
          <div className="bg-emerald-50 text-emerald-700 px-4 py-2 text-xs font-bold uppercase tracking-wider">Earnings</div>
          <div className="text-xs">
            <PayslipRow label="Basic Pay" value={fmtMoney(earnings.basic, cur)} />
            {earnings.overtime_pay ? (
              <PayslipRow label={`Overtime (${earnings.overtime_hours}h)`} value={fmtMoney(earnings.overtime_pay, cur)} />
            ) : null}
            {keyed(allowances, a => a.name).map(({ item: a, key }) => (
              <PayslipRow
                key={key}
                label={
                  <span>
                    {a.name}{' '}
                    <Badge variant="outline" className="ml-1 text-[9px] font-normal">
                      {a.taxable ? 'Taxable' : 'Non-tax'}
                    </Badge>
                  </span>
                }
                value={fmtMoney(a.amount, cur)}
              />
            ))}
            <PayslipRow label="Gross Pay" value={fmtMoney(earnings.gross_pay, cur)} total />
          </div>
        </div>
        <div>
          <div className="bg-red-50 text-red-700 px-4 py-2 text-xs font-bold uppercase tracking-wider">Deductions</div>
          <div className="text-xs">
            {deductions.ssnit_employee ? (
              <PayslipRow label="SSNIT (employee 5.5%)" value={fmtMoney(deductions.ssnit_employee, cur)} />
            ) : null}
            {deductions.paye ? <PayslipRow label="PAYE income tax" value={fmtMoney(deductions.paye, cur)} /> : null}
            {keyed(otherItems, d => d.name).map(({ item: d, key }) => <PayslipRow key={key} label={d.name} value={fmtMoney(d.amount, cur)} />)}
            {!deductions.ssnit_employee && !deductions.paye && otherItems.length === 0 && (
              <div className="px-4 py-3 text-muted-foreground italic">No deductions</div>
            )}
            <PayslipRow label="Total Deductions" value={fmtMoney(deductions.total, cur)} total />
          </div>
        </div>
      </div>

      {/* NET PAY hero */}
      <div
        className="px-6 py-4 flex items-center justify-between text-white"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${shade(primary, -12)} 100%)` }}
      >
        <div>
          <div className="text-xs uppercase tracking-wider opacity-80">Net Pay</div>
          <div className="text-[11px] opacity-80 mt-0.5">Take-home for {period}</div>
        </div>
        <div className="text-2xl font-bold tabular-nums">{fmtMoney(slip.net_pay, cur)}</div>
      </div>

      {/* Statutory + employer summary */}
      <div className="px-6 py-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        <div>
          <div className="font-semibold mb-2 uppercase text-[10px] tracking-wider text-slate-500">Statutory</div>
          <div className="space-y-1 text-slate-600">
            <div className="flex justify-between"><span>Taxable income</span><span className="tabular-nums">{fmtMoney(slip.taxable_income, cur)}</span></div>
            <div className="flex justify-between"><span>SSNIT employer (13%)</span><span className="tabular-nums">{fmtMoney(employer.ssnit_employer, cur)}</span></div>
            <div className="flex justify-between"><span>Tier-2 employer</span><span className="tabular-nums">{fmtMoney(employer.tier2_employer, cur)}</span></div>
            <div className="flex justify-between font-medium border-t pt-1 mt-1 text-slate-800">
              <span>Total employer cost</span><span className="tabular-nums">{fmtMoney(employer.total, cur)}</span>
            </div>
          </div>
        </div>
        <div>
          <div className="font-semibold mb-2 uppercase text-[10px] tracking-wider text-slate-500">Payment</div>
          <div className="space-y-1 text-slate-600">
            <Kv label="Bank" value={bank.bank_name} />
            <Kv label="Account" value={bank.account_number} />
            <Kv label="Branch" value={bank.branch} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t px-6 py-3 flex justify-between items-center text-[10px] text-slate-500">
        <span>This is a computer-generated payslip and does not require a signature.</span>
        <span className="italic" style={{ color: accent }}>Powered by {businessName} HR &amp; Payroll</span>
      </div>
    </div>
  );
}

function PayslipRow({ label, value, total = false }) {
  const cls = total
    ? 'flex justify-between px-4 py-2 bg-slate-50 border-t font-bold text-slate-800'
    : 'flex justify-between px-4 py-1.5 border-b last:border-b-0';
  return (
    <div className={cls}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function Kv({ label, value, bold = false, cap = false }) {
  return (
    <div className="flex flex-col">
      <span className="uppercase text-[10px] text-slate-500 tracking-wider">{label}</span>
      <span className={`${bold ? 'font-bold text-base' : 'font-semibold'} ${cap ? 'capitalize' : ''} text-slate-800 mt-0.5`}>
        {value || '—'}
      </span>
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? 'bg-primary/5 border-primary/30' : ''}`}>
      <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
      <p className={`text-base font-semibold tabular-nums ${highlight ? 'text-primary' : ''}`}>{value}</p>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Branding / print helpers
// ─────────────────────────────────────────────────────────────────────

// Lighten/darken hex color by percent (-100..100)
function shade(hex, percent) {
  if (!hex || typeof hex !== 'string') return '#0d9b8c';
  const m = hex.replace('#', '');
  const num = parseInt(m.length === 3 ? m.split('').map(c => c + c).join('') : m, 16);
  if (Number.isNaN(num)) return hex;
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  const t = percent < 0 ? 0 : 255;
  const p = Math.abs(percent) / 100;
  r = Math.round((t - r) * p) + r;
  g = Math.round((t - g) * p) + g;
  b = Math.round((t - b) * p) + b;
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

const escHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

function buildPayslipPrintDocument({ slip, employee, period, settings }) {
  const cur = slip.currency || 'GHS';
  const fmt = (v) => `${cur} ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const earnings = slip.earnings || {};
  const deductions = slip.deductions || {};
  const employer = slip.employer_costs || {};
  const allowances = earnings.allowances || [];
  const otherItems = deductions.other_items || [];
  const bank = (employee?.bank) || (slip.bank) || {};

  const businessName = settings?.business_name || 'DutiLyne';
  const tagline = settings?.tagline || 'HR & Payroll';
  const logoUrl = settings?.logo_url || '';
  const primary = settings?.primary_color || '#0d9b8c';
  const accent = settings?.accent_color || '#e8590c';
  const primaryDark = shade(primary, -18);
  const primarySoft = shade(primary, 78);
  const address = settings?.business_address || '';
  const phone = settings?.contact_phone || '';
  const email = settings?.contact_email || '';
  const issued = new Date().toLocaleDateString();
  const docNo = `PS-${(slip.payslip_id || '').slice(-6).toUpperCase()}`;

  const earnRows = [];
  earnRows.push({ label: 'Basic Pay', value: fmt(earnings.basic) });
  if (earnings.overtime_pay) {
    earnRows.push({ label: `Overtime${earnings.overtime_hours ? ` (${earnings.overtime_hours}h)` : ''}`, value: fmt(earnings.overtime_pay) });
  }
  for (const a of allowances) {
    earnRows.push({ label: `${a.name}${a.taxable ? '' : ' (non-tax)'}`, value: fmt(a.amount) });
  }

  const dedRows = [];
  if (deductions.ssnit_employee) dedRows.push({ label: 'SSNIT (employee 5.5%)', value: fmt(deductions.ssnit_employee) });
  if (deductions.paye) dedRows.push({ label: 'PAYE income tax', value: fmt(deductions.paye) });
  for (const d of otherItems) dedRows.push({ label: d.name, value: fmt(d.amount) });

  const renderRows = (rows) => rows.length
    ? rows.map(r => `<tr><td class="lbl">${escHtml(r.label)}</td><td class="amt">${escHtml(r.value)}</td></tr>`).join('')
    : `<tr><td class="lbl muted" colspan="2"><em>None</em></td></tr>`;

  const logoBlock = logoUrl
    ? `<img src="${escHtml(logoUrl)}" alt="logo" class="logo" />`
    : `<div class="logo-fallback">${escHtml(businessName.slice(0, 1).toUpperCase())}</div>`;

  const contactLine = [phone, email].filter(Boolean).map(escHtml).join(' &nbsp;·&nbsp; ');

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Payslip · ${escHtml(slip.employee_name || '')} · ${escHtml(period || '')}</title>
<style>
  @page { size: A4; margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0;
    font-family: "Helvetica Neue", "Calibri", "Segoe UI", Arial, sans-serif;
    color: #1f2937;
    background: #ffffff;
    font-size: 11pt;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .page { max-width: 190mm; margin: 0 auto; padding: 0; }

  /* Letterhead */
  .letterhead {
    display: flex; justify-content: space-between; align-items: flex-start;
    padding: 18px 24px; border-radius: 6px;
    background: linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%);
    color: #ffffff;
  }
  .brand { display: flex; gap: 14px; align-items: center; }
  .logo, .logo-fallback {
    width: 56px; height: 56px; border-radius: 8px;
    background: rgba(255,255,255,0.14);
    display: flex; align-items: center; justify-content: center;
    font-size: 24px; font-weight: 700; color: #ffffff;
    object-fit: contain; padding: 4px;
  }
  .biz-name { font-size: 18pt; font-weight: 700; letter-spacing: 0.2px; line-height: 1.1; }
  .biz-tag { font-size: 9pt; opacity: 0.85; margin-top: 2px; }
  .biz-meta { font-size: 8.5pt; opacity: 0.85; margin-top: 6px; line-height: 1.4; }
  .doc-title { text-align: right; }
  .doc-title .h { font-size: 22pt; font-weight: 700; letter-spacing: 2px; }
  .doc-title .sub { font-size: 9pt; opacity: 0.9; margin-top: 4px; }

  /* Meta strip */
  .meta-strip {
    margin-top: 14px; padding: 10px 16px;
    border: 1px solid #e5e7eb; border-radius: 6px;
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
    background: #f8fafc;
  }
  .meta-strip .item .k { font-size: 8pt; color: #6b7280; text-transform: uppercase; letter-spacing: 0.6px; }
  .meta-strip .item .v { font-size: 10pt; color: #111827; font-weight: 600; margin-top: 2px; }

  /* Section title */
  .section { margin-top: 16px; }
  .section-h {
    font-size: 9.5pt; font-weight: 700; letter-spacing: 0.8px; text-transform: uppercase;
    color: ${primary};
    border-bottom: 2px solid ${primary};
    padding-bottom: 4px; margin-bottom: 8px;
  }

  /* Two-column employee */
  .emp-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px;
    font-size: 10pt;
  }
  .emp-grid .row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dotted #e5e7eb; }
  .emp-grid .row .k { color: #6b7280; }
  .emp-grid .row .v { color: #111827; font-weight: 600; text-align: right; }

  /* Earnings / Deductions tables */
  .pay-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  table.ledger { width: 100%; border-collapse: collapse; font-size: 10pt; }
  table.ledger thead th {
    text-align: left; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.6px;
    color: #ffffff; background: ${primary}; padding: 6px 10px; font-weight: 600;
  }
  table.ledger thead th.amt { text-align: right; }
  table.ledger tbody td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; }
  table.ledger tbody td.amt { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  table.ledger tbody td.muted { color: #9ca3af; }
  table.ledger tfoot td {
    padding: 8px 10px; font-weight: 700; background: ${primarySoft};
    border-top: 2px solid ${primary}; color: #111827;
  }
  table.ledger tfoot td.amt { text-align: right; font-variant-numeric: tabular-nums; }

  /* Net pay band */
  .netpay {
    margin-top: 16px; padding: 16px 22px; border-radius: 6px;
    background: linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%);
    color: #ffffff;
    display: flex; align-items: center; justify-content: space-between;
  }
  .netpay .k { font-size: 10pt; text-transform: uppercase; letter-spacing: 1.5px; opacity: 0.9; }
  .netpay .sub { font-size: 8.5pt; opacity: 0.8; margin-top: 2px; }
  .netpay .amount { font-size: 22pt; font-weight: 700; font-variant-numeric: tabular-nums; }

  /* Footer payment + statutory */
  .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; margin-top: 18px; font-size: 10pt; }
  .summary-grid .box .h {
    font-size: 9pt; text-transform: uppercase; letter-spacing: 0.7px;
    color: #6b7280; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 6px;
  }
  .summary-grid .box .row { display: flex; justify-content: space-between; padding: 3px 0; }
  .summary-grid .box .row .k { color: #4b5563; }
  .summary-grid .box .row .v { color: #111827; font-weight: 600; font-variant-numeric: tabular-nums; }
  .summary-grid .box .row.total { border-top: 1px solid #e5e7eb; margin-top: 4px; padding-top: 6px; font-weight: 700; }

  /* Sign-off */
  .signoff { margin-top: 26px; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; }
  .signoff .line { border-top: 1px solid #9ca3af; padding-top: 6px; font-size: 9pt; color: #6b7280; }

  /* Footer */
  .doc-footer {
    margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 8px;
    display: flex; justify-content: space-between; align-items: center;
    font-size: 8pt; color: #6b7280;
  }
  .doc-footer .accent { color: ${accent}; font-style: italic; }

  @media print {
    .no-print { display: none !important; }
    .page { max-width: 100%; }
    .letterhead, .netpay { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
  <div class="page">
    <!-- Letterhead -->
    <div class="letterhead">
      <div class="brand">
        ${logoBlock}
        <div>
          <div class="biz-name">${escHtml(businessName)}</div>
          <div class="biz-tag">${escHtml(tagline)}</div>
          ${address ? `<div class="biz-meta">${escHtml(address)}</div>` : ''}
          ${contactLine ? `<div class="biz-meta">${contactLine}</div>` : ''}
        </div>
      </div>
      <div class="doc-title">
        <div class="h">PAYSLIP</div>
        <div class="sub">Period · ${escHtml(period || '')}</div>
        <div class="sub">Issued · ${escHtml(issued)}</div>
        <div class="sub">Doc # ${escHtml(docNo)}</div>
      </div>
    </div>

    <!-- Meta strip -->
    <div class="meta-strip">
      <div class="item"><div class="k">Employee</div><div class="v">${escHtml(slip.employee_name || employee?.name || '—')}</div></div>
      <div class="item"><div class="k">Employee ID</div><div class="v">${escHtml(slip.employee_id || '—')}</div></div>
      <div class="item"><div class="k">Department</div><div class="v">${escHtml(employee?.department || '—')}</div></div>
      <div class="item"><div class="k">Contract</div><div class="v">${escHtml((slip.contract_type || 'full_time').replace('_', ' '))}</div></div>
    </div>

    <!-- Employee details -->
    <div class="section">
      <div class="section-h">Employee details</div>
      <div class="emp-grid">
        <div class="row"><span class="k">SSNIT #</span><span class="v">${escHtml(employee?.ssnit_number || '—')}</span></div>
        <div class="row"><span class="k">TIN</span><span class="v">${escHtml(employee?.tin || '—')}</span></div>
        <div class="row"><span class="k">Email</span><span class="v">${escHtml(employee?.email || '—')}</span></div>
        <div class="row"><span class="k">Phone</span><span class="v">${escHtml(employee?.phone || '—')}</span></div>
      </div>
    </div>

    <!-- Earnings + Deductions -->
    <div class="section">
      <div class="pay-grid">
        <div>
          <table class="ledger">
            <thead><tr><th>Earnings</th><th class="amt">Amount</th></tr></thead>
            <tbody>${renderRows(earnRows)}</tbody>
            <tfoot><tr><td>Gross Pay</td><td class="amt">${escHtml(fmt(earnings.gross_pay))}</td></tr></tfoot>
          </table>
        </div>
        <div>
          <table class="ledger">
            <thead><tr><th>Deductions</th><th class="amt">Amount</th></tr></thead>
            <tbody>${renderRows(dedRows)}</tbody>
            <tfoot><tr><td>Total Deductions</td><td class="amt">${escHtml(fmt(deductions.total))}</td></tr></tfoot>
          </table>
        </div>
      </div>
    </div>

    <!-- Net pay -->
    <div class="netpay">
      <div>
        <div class="k">Net Pay</div>
        <div class="sub">Take-home for ${escHtml(period || '')}</div>
      </div>
      <div class="amount">${escHtml(fmt(slip.net_pay))}</div>
    </div>

    <!-- Statutory + Payment -->
    <div class="summary-grid">
      <div class="box">
        <div class="h">Statutory contributions (employer)</div>
        <div class="row"><span class="k">Taxable income</span><span class="v">${escHtml(fmt(slip.taxable_income))}</span></div>
        <div class="row"><span class="k">SSNIT employer (13%)</span><span class="v">${escHtml(fmt(employer.ssnit_employer))}</span></div>
        <div class="row"><span class="k">Tier-2 employer</span><span class="v">${escHtml(fmt(employer.tier2_employer))}</span></div>
        <div class="row total"><span class="k">Total employer cost</span><span class="v">${escHtml(fmt(employer.total))}</span></div>
      </div>
      <div class="box">
        <div class="h">Payment details</div>
        <div class="row"><span class="k">Bank</span><span class="v">${escHtml(bank.bank_name || '—')}</span></div>
        <div class="row"><span class="k">Account</span><span class="v">${escHtml(bank.account_number || '—')}</span></div>
        <div class="row"><span class="k">Branch</span><span class="v">${escHtml(bank.branch || '—')}</span></div>
        <div class="row"><span class="k">Pay date</span><span class="v">${escHtml(slip.pay_date || issued)}</span></div>
      </div>
    </div>

    <!-- Sign-off -->
    <div class="signoff">
      <div class="line">Authorised by (HR / Finance)</div>
      <div class="line">Received by (Employee signature)</div>
    </div>

    <!-- Footer -->
    <div class="doc-footer">
      <span>This is a computer-generated payslip and does not require a signature.</span>
      <span class="accent">${escHtml(businessName)} HR &amp; Payroll</span>
    </div>
  </div>
</body>
</html>`;
}
